// 👇️ ts-nocheck
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Table as AntTable } from "antd";
import { notify } from "@/shared/utils/notify";
import { GripVertical } from "lucide-react";
import { productCategoriesService } from "../../../shared/services";
import { IProductCategory } from "../../../redux/@types";

function createDraggableWrapper(rowKeys: string[]) {
  return function DraggableWrapper(props: any) {
    const { children, ...restProps } = props;
    return (
      <SortableContext
        items={rowKeys}
        strategy={verticalListSortingStrategy}
      >
        <tbody {...restProps} style={{ ...restProps.style, pointerEvents: "auto" }}>
          {children}
        </tbody>
      </SortableContext>
    );
  };
}

function DraggableRow(props: any) {
  const rowKey = props["data-row-key"] ?? props.record?.id ?? props.index;
  const id = String(rowKey);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const { children, ...restProps } = props;
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging
      ? { zIndex: 999, background: "#fafafa", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }
      : {}),
  };

  // Solo la primera celda (asa de arrastre) inicia el drag; el resto permite clics en botones.
  const cells = React.Children.map(children, (child, index) => {
    if (index === 0 && React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<any>, {
        ...attributes,
        ...listeners,
        style: {
          ...(child.props?.style ?? {}),
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "none",
        },
      });
    }
    return child;
  });

  return (
    <tr ref={setNodeRef} {...restProps} style={style}>
      {cells}
    </tr>
  );
}

interface Props {
  columns: any[];
  parent_id?: number;
  onLoad?: (props: { refresh: () => void }) => void;
}

export const TableProductCategoriesSortable = ({
  columns,
  parent_id,
  onLoad,
}: Props) => {
  const [data, setData] = useState<IProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const isSubcategoryView = parent_id !== undefined && parent_id !== null;

      const res = await productCategoriesService.find({
        query: isSubcategoryView
          ? {
              $limit: 1000,
              $sort: { position: -1, id: -1 },
              parent_id,
            }
          : {
              // Sin filtro parent_id en API: Feathers no resuelve bien parent_id IS NULL.
              // Traemos todas y filtramos en cliente (hijas de raíz + huérfanas).
              $limit: 1000,
              $sort: { position: -1, id: -1 },
            },
      });

      const raw = (res as any)?.data ?? (Array.isArray(res) ? res : []);
      let items: IProductCategory[] = raw;

      if (!isSubcategoryView) {
        items = items.filter(
          (c) =>
            c.id !== 1 &&
            (c.parent_id === 1 || c.parent_id === null || c.parent_id === undefined)
        );
      }

      items = [...items].sort((a, b) => {
        const posDiff = (b.position ?? 0) - (a.position ?? 0);
        return posDiff !== 0 ? posDiff : b.id - a.id;
      });

      setData(items);
    } catch (err) {
      notify.error("Error al cargar categorías");
    } finally {
      setLoading(false);
    }
  }, [parent_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    onLoad?.({ refresh: fetchData });
  }, [onLoad, fetchData]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((item) => String(item.id) === active.id);
    const newIndex = data.findIndex((item) => String(item.id) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(data, oldIndex, newIndex);
    const withPositions = reordered.map((item, index) => ({
      ...item,
      position: reordered.length - index,
    }));
    setData(withPositions);

    try {
      await Promise.all(
        withPositions.map((item) =>
          productCategoriesService.patch(item.id, { position: item.position })
        )
      );
      notify.success("Orden actualizado correctamente");
    } catch (err: any) {
      notify.error(err?.message || "Error al actualizar orden");
      fetchData();
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const dragHandleColumn = {
    key: "dragHandle",
    dataIndex: "dragHandle",
    title: "",
    width: 48,
    render: () => (
      <span
        title="Arrastra para reordenar"
        style={{
          color: "#999",
          display: "inline-flex",
          padding: "4px",
        }}
      >
        <GripVertical size={20} />
      </span>
    ),
  };

  const allColumns = [dragHandleColumn, ...columns];

  const bodyWrapper = useMemo(
    () => createDraggableWrapper(data.map((d) => String(d.id))),
    [data]
  );

  const activeItem = activeId ? data.find((d) => String(d.id) === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div style={{ margin: "15px 0", width: "100%", alignSelf: "stretch" }}>
        <p style={{ marginBottom: 8, color: "#666", fontSize: 13 }}>
          Arrastra una fila para cambiar el orden de las categorías.
        </p>
        <AntTable<IProductCategory>
          className="tableStyleOne modern-pagination"
          columns={allColumns}
          dataSource={data}
          loading={loading}
          rowKey={(record) => String(record.id)}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100", "200"],
            showTotal: (t) => `${t} categorías`,
          }}
          components={{
            body: {
              wrapper: bodyWrapper,
              row: DraggableRow,
            },
          }}
        />
        <DragOverlay>
          {activeItem ? (
            <div
              style={{
                background: "#fff",
                padding: "12px 16px",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                cursor: "grabbing",
                border: "1px solid #e8e8e8",
              }}
            >
              {activeItem.name}
            </div>
          ) : null}
        </DragOverlay>
      </div>
    </DndContext>
  );
};
